// Customer "My requests" — list of their tickets. Clicking a row opens the
// detail (status, agent replies, ability to reply back). Defaults to showing
// only Open (active) requests; filter to see Resolved / Closed / All.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus } from 'lucide-react';
import { usePortal } from './PortalLayout';
import { StatusBadge, PriorityBadge } from '../../components/serviceDesk/ticketMeta';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const isClosed = (s) => s === 'closed';
const isResolved = (s) => s === 'resolved';
const isOpen = (s) => !isClosed(s) && !isResolved(s);

const FILTERS = [
  { key: 'open', label: 'Open', match: (t) => isOpen(t.status) },
  { key: 'resolved', label: 'Resolved', match: (t) => isResolved(t.status) },
  { key: 'closed', label: 'Closed', match: (t) => isClosed(t.status) },
  { key: 'all', label: 'All', match: () => true },
];

export default function PortalMyRequestsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { customer, customerChecked } = usePortal();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');

  useEffect(() => {
    if (!customerChecked) return; // wait until the session check completes
    if (!customer) { navigate(`/help/${slug}/login`); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/customer/tickets`, { withCredentials: true });
        setTickets(data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [customer, customerChecked, slug, navigate]);

  const counts = useMemo(() => {
    const c = { open: 0, resolved: 0, closed: 0, all: tickets.length };
    tickets.forEach((t) => { if (isOpen(t.status)) c.open++; else if (isResolved(t.status)) c.resolved++; else if (isClosed(t.status)) c.closed++; });
    return c;
  }, [tickets]);

  const visible = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter) || FILTERS[0];
    return tickets.filter(f.match);
  }, [tickets, filter]);

  if (loading) return <p className="text-zinc-400 text-sm">Loading…</p>;

  return (
    <div data-testid="portal-my-requests">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-bold text-zinc-900 font-['Outfit']">My requests</h1>
        <Link to={`/help/${slug}`}
          className="inline-flex items-center gap-1.5 text-white text-sm font-medium px-4 py-2.5 rounded-md shadow-sm hover:opacity-90 transition-opacity"
          style={{ background: 'var(--theme, #2563eb)' }}
          data-testid="portal-create-request-btn">
          <Plus className="w-4 h-4" /> Create request
        </Link>
      </div>

      {/* Status filter */}
      <div className="flex items-center gap-1.5 mb-4" data-testid="portal-requests-filter">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            data-testid={`portal-filter-${f.key}`}
            className={`text-sm font-medium px-3.5 py-1.5 rounded-full transition-colors ${
              filter === f.key ? 'bg-zinc-900 text-white' : 'bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}>
            {f.label}
            <span className={`ml-1.5 text-xs ${filter === f.key ? 'text-zinc-300' : 'text-zinc-400'}`}>{counts[f.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-16 text-center">
          <p className="text-base text-zinc-500">
            {filter === 'open' ? "You have no open requests." : `No ${filter} requests.`}
          </p>
          <Link to={`/help/${slug}`} className="text-blue-600 underline text-sm mt-2 inline-block">Browse request types</Link>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full">
            <thead className="bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
              <tr>
                <th className="text-left py-3.5 px-6 font-semibold w-32">Key</th>
                <th className="text-left py-3.5 px-6 font-semibold">Title</th>
                <th className="text-left py-3.5 px-6 font-semibold w-32">Priority</th>
                <th className="text-left py-3.5 px-6 font-semibold w-32">Status</th>
                <th className="text-left py-3.5 px-6 font-semibold w-36">Created</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((t) => (
                <tr key={t.ticket_id} onClick={() => navigate(`/help/${slug}/requests/${t.ticket_key}`)}
                  className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50 cursor-pointer transition-colors"
                  data-testid={`portal-my-ticket-${t.ticket_id}`}>
                  <td className="py-4 px-6 font-mono text-sm text-blue-700 font-semibold">{t.ticket_key}</td>
                  <td className="py-4 px-6 text-[15px] text-zinc-900 font-medium">{t.title}</td>
                  <td className="py-4 px-6">{t.priority ? <PriorityBadge priority={t.priority} /> : <span className="text-zinc-300 text-sm">—</span>}</td>
                  <td className="py-4 px-6"><StatusBadge status={t.status} /></td>
                  <td className="py-4 px-6 text-sm text-zinc-500">
                    {new Date(t.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
